import Box from "@mui/material/Box";
import Typography, { type TypographyProps } from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TikTokIcon from "../../components/icons/TikTokIcon";
import { useTranslation } from "react-i18next";

const SectionTitle = styled(Typography)(({ theme }) => ({
  // && beats MuiTypography-body1 theme fontFamily (Inter / DM Sans) so DM Serif Display applies.
  "&&": {
    color: theme.palette.primary.main,
    fontSize: "1.25rem",
    fontWeight: 700,
    fontFamily: '"DM Serif Display", serif',
    margin: 0,
    padding: 0,
    // Tight line box so the glyph optically centers with the platform icon.
    lineHeight: 1,
    paddingTop: "0.12em",
    paddingBottom: "0.08em",
    overflow: "visible",
  },
  "@media (min-width: 600px)": {
    "&&": {
      fontSize: "1.75rem",
    },
  },
}));

// Wrapper sets font-size; icons use inherit so SvgIcon + custom TikTokIcon size match the title.
const ICON_CELL_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  lineHeight: 0,
  fontSize: { xs: "1.25rem", sm: "1.75rem" },
};

const ICON_SX: SxProps<Theme> = {
  color: (theme) => theme.palette.getContrastText(theme.palette.background.default),
  fontSize: "inherit",
  display: "block",
  flexShrink: 0,
};

export type MediaKitPlatformSectionHeaderPlatform =
  | "youtube"
  | "instagram"
  | "tiktok"
  | "podscope";

function platformLabel(
  platform: MediaKitPlatformSectionHeaderPlatform,
  podscopeData: string,
): string {
  if (platform === "podscope") return podscopeData;
  if (platform === "youtube") return "YouTube";
  if (platform === "instagram") return "Instagram";
  return "TikTok";
}

export type MediaKitPlatformSectionHeaderProps = {
  platform: MediaKitPlatformSectionHeaderPlatform;
  sx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
};

export function MediaKitPlatformSectionHeader({
  platform,
  sx,
  titleSx,
}: MediaKitPlatformSectionHeaderProps) {
  const { t } = useTranslation("mediaKit");
  const label = platformLabel(platform, t("podscopeData"));

  const showIcon =
    platform === "youtube" ||
    platform === "instagram" ||
    platform === "tiktok";

  return (
    <Box
      data-mediakit-pdf-platform-header=""
      sx={{
        // Grid + center; PDF-only optical nudges run in mediaKitPdfExport applyCloneCaptureFixes.
        display: "grid",
        gridTemplateColumns: showIcon ? "auto minmax(0,1fr)" : "minmax(0,1fr)",
        alignItems: "center",
        columnGap: 1,
        ...sx,
      }}
    >
      {showIcon && (
        <Box
          data-mediakit-pdf-platform-icon-nudge={
            platform === "tiktok" ? "tiktok" : "standard"
          }
          sx={ICON_CELL_SX}
        >
          {platform === "youtube" && <YouTubeIcon sx={ICON_SX} />}
          {platform === "instagram" && <InstagramIcon sx={ICON_SX} />}
          {platform === "tiktok" && (
            <TikTokIcon fontSize="inherit" sx={ICON_SX} />
          )}
        </Box>
      )}
      <Box
        data-mediakit-pdf-platform-title=""
        sx={{
          display: "block",
          minWidth: 0,
        }}
      >
        <SectionTitle
          {...({ component: "span" } as TypographyProps)}
          data-mediakit-pdf-platform-label=""
          sx={titleSx}
        >
          {label}
        </SectionTitle>
      </Box>
    </Box>
  );
}
